import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

const SmartRedirect = () => {
	const location = useLocation()
	const { openProjectName } = useSelector(state => state.user)
	const { isLogged } = useSelector(state => state.auth)
	
	const isInvitePage = location.pathname.includes('/invite/accept') || location.pathname.includes('/invite/profile')
	if (isInvitePage) {
		return null
	}
	
	const hasAuthError = new URLSearchParams(location.search).has("auth_error");
	if (hasAuthError) {
		return null;
	}
	
	// If user is not logged in, redirect to login
	if (!isLogged) {
		return <Navigate to='/login' replace />
	}
	
	// Check if the current path is exactly '/profile/sso_complete'
	if (location.pathname === '/profile/sso_complete') {
		return <Navigate to='/profile/sso_complete' replace />
	}
	
	// Check if user is returning from payment (various possible return URLs)
	const isPaymentReturn = location.pathname === '/return' || 
		location.pathname.startsWith('/payments/subscription_bin_success/') ||
		location.pathname.startsWith('/api/payments/subscription_bin_success/') ||
		location.pathname.startsWith('/payments/') && location.search.includes('payment_id') ||
		location.pathname.startsWith('/api/payments/') && location.search.includes('payment_id');
	
	if (isPaymentReturn) {
		// Extract payment_id from URL
		const searchParams = new URLSearchParams(location.search);
		const paymentId = searchParams.get('payment_id');
		
		// Save payment_id to localStorage if found
		if (paymentId) {
			localStorage.setItem('payment_id', paymentId);
		}
		
		// Check URL parameters for return_url
		const returnUrlFromParams = searchParams.get('return_url');
		
		// Check new payment_data with timestamp validation
		const savedPaymentData = localStorage.getItem('payment_data');
		let validPaymentData = null;
		
		if (savedPaymentData) {
			try {
				const paymentData = JSON.parse(savedPaymentData);
				const now = Date.now();
				const dataAge = now - paymentData.timestamp;
				const maxAge = 3600000; // 1 hour in milliseconds
				
				// Validate timestamp (data should be less than 1 hour old)
				if (dataAge < maxAge) {
					// Validate domain (should match current domain)
					if (paymentData.domain === window.location.hostname) {
						validPaymentData = paymentData;
					} else {
						localStorage.removeItem('payment_data');
					}
				} else {
					localStorage.removeItem('payment_data');
				}
			} catch (error) {
				localStorage.removeItem('payment_data');
			}
		}
		
		// Check old localStorage as fallback
		const savedReturnUrl = localStorage.getItem('payment_return_url');
		
		// Use URL parameter first, then new payment data, then old localStorage as fallback
		const finalReturnUrl = returnUrlFromParams || (validPaymentData && validPaymentData.returnUrl) || savedReturnUrl;
		
		if (finalReturnUrl) {
			// Save payment_id and sbs_type before clearing localStorage
			const searchParams = new URLSearchParams(location.search);
			const paymentId = searchParams.get('payment_id');
			if (paymentId) {
				localStorage.setItem('payment_id', paymentId);
			}
			
			// Save sbs_type from payment data to correct localStorage key
			if (validPaymentData && validPaymentData.subscriptionData && validPaymentData.subscriptionData.sbs_type) {
				const sbsType = validPaymentData.subscriptionData.sbs_type;
				if (sbsType === 'sales_tax_subs') {
					localStorage.setItem('sales_tax_sbs_type', sbsType);
				} else if (sbsType === 'epr_subs') {
					localStorage.setItem('epr_sbs_type', sbsType);
				} else {
					localStorage.setItem('sbs_type', sbsType);
				}
			}
			
			// Don't clear localStorage yet - let it persist for the target page
			// Full URL (e.g. after payment return to same domain): use window.location
			if (finalReturnUrl.startsWith('http://') || finalReturnUrl.startsWith('https://')) {
				window.location.href = finalReturnUrl;
				return null;
			}
			return <Navigate to={finalReturnUrl} replace />
		} else {
			// No return URL found, but we have payment_id - save it and redirect to default page
			const searchParams = new URLSearchParams(location.search);
			const paymentId = searchParams.get('payment_id');
			if (paymentId) {
				localStorage.setItem('payment_id', paymentId);
				
				// Save sbs_type from payment data to correct localStorage key
				if (validPaymentData && validPaymentData.subscriptionData && validPaymentData.subscriptionData.sbs_type) {
					const sbsType = validPaymentData.subscriptionData.sbs_type;
					if (sbsType === 'sales_tax_subs') {
						localStorage.setItem('sales_tax_sbs_type', sbsType);
					} else if (sbsType === 'epr_subs') {
						localStorage.setItem('epr_sbs_type', sbsType);
					} else {
						localStorage.setItem('sbs_type', sbsType);
					}
				}
				
				// Redirect to default page based on project
				let defaultRoute = '/global-settings';
				if (openProjectName === 'LEPR') {
					defaultRoute = '/epr-reporting/add';
				} else if (openProjectName === 'LInvoice') {
					defaultRoute = '/documents';
				}
				
				return <Navigate to={defaultRoute} replace />
			}
		}
	}
	
	// Check if user is returning from payment and redirect to saved URL (if not on payment return page)
	const savedReturnUrl = localStorage.getItem('payment_return_url')
	
	if (savedReturnUrl) {
		// Check if we're already on the target page
		const isFullUrl = savedReturnUrl.startsWith('http://') || savedReturnUrl.startsWith('https://')
		const isCurrentPage = isFullUrl
			? (window.location.href === savedReturnUrl || window.location.href === savedReturnUrl.replace(/\/$/, ''))
			: (location.pathname === savedReturnUrl || location.pathname + location.search === savedReturnUrl)
		if (isCurrentPage) {
			localStorage.removeItem('payment_return_url')
			return null // Don't redirect, just stay on current page
		}
		
		// Don't clear localStorage yet - let it persist for the target page
		if (isFullUrl) {
			window.location.href = savedReturnUrl
			return null
		}
		return <Navigate to={savedReturnUrl} replace />
	}
	
	// Check if there's a close parameter in the URL
	const searchParams = new URLSearchParams(location.search)
	const closeParam = searchParams.get('close')
	
	if (closeParam === 'True' || closeParam === 'true' || closeParam === '1') {
		return <Navigate to='/profile/sso_complete' replace />
	}
	
	// Check if this is a project switch (URL contains project-specific domain or parameters)
	const isProjectSwitch = location.search.includes('project') || 
		location.search.includes('app_id') || 
		location.pathname === '/' ||
		location.pathname === ''
	
	// If it's a project switch, redirect to global settings hub
	if (isProjectSwitch) {
		return <Navigate to='/global-settings' replace />
	}
	
	// Redirect based on current project for other cases
	let defaultRoute = '/global-settings' // fallback
	
	if (openProjectName === 'LInvoice') {
		defaultRoute = '/global-settings'
	} else if (openProjectName === 'BASIC') {
		defaultRoute = '/global-settings'
	} else if (openProjectName === 'LEPR') {
		defaultRoute = '/global-settings'
	} else {
		defaultRoute = '/global-settings'
	}
	
	return <Navigate to={defaultRoute} replace />
	// return <Navigate to={'/transactions'} replace />
}

export default SmartRedirect
